import { TextField, TextFieldProps, styled, Box, Tooltip, InputAdornment } from "@mui/material";
import { tokens } from "../../theme/tokens";
import { useState } from "react";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";

interface TrackNowTextFieldProps extends Omit<TextFieldProps, 'variant'> {
  success?: boolean;
  successMessage?: string;
  showDisabledTooltip?: boolean;
}

const StyledTextField = styled(TextField)<TrackNowTextFieldProps>(({ theme, success, multiline, error, type }) => ({
  '& .MuiOutlinedInput-root': {
    height: multiline ? 'auto' : type === 'file' ? '50px' : '48px',
    minHeight: multiline ? '96px' : type === 'file' ? '50px' : '48px',
    borderRadius: '10px',
    fontFamily: 'Area Normal, Inter, sans-serif',
    fontSize: '16px',
    fontWeight: 400,
    backgroundColor: tokens.colors.offWhite,
    
    '& fieldset': {
      borderColor: tokens.colors.grey,
      borderWidth: '1px',
    },
    
    '&:hover:not(.Mui-disabled) fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1px',
    },
    
    '&.Mui-focused fieldset': {
      borderColor: `${theme.palette.primary.main} !important`,
      borderWidth: '2px !important',
    },
    
    '&.Mui-error fieldset': {
      borderColor: tokens.colors.grey,
      borderWidth: '1px',
    },
    
    '&.Mui-disabled': {
      backgroundColor: tokens.colors.offWhite,
      '& fieldset': {
        borderColor: tokens.colors.grey,
        borderWidth: '1px',
      },
      '& input, & textarea': {
        color: tokens.colors.grey,
        cursor: 'not-allowed',
        WebkitTextFillColor: tokens.colors.grey,
      },
    },
    
    '& input, & textarea': {
      padding: type === 'file' ? '10px 12px' : '12px 14px',
      color: tokens.colors.offBlack,
      lineHeight: type === 'file' ? 1.4 : 'normal',
      '&::placeholder': {
        color: 'rgba(33, 33, 33, 0.6)',
        opacity: 1,
      },
    },

    '& input[type="file"]': {
      padding: '8px 10px',
      fontSize: '14px',
      lineHeight: 1.4,
      height: '100%',
      boxSizing: 'border-box',
      display: 'block',
    },

    '& input[type="file"]::file-selector-button': {
      marginRight: '10px',
      padding: '6px 10px',
      height: '34px',
      lineHeight: 1.2,
      verticalAlign: 'middle',
      borderRadius: '6px',
      border: `1px solid ${tokens.colors.grey}`,
      backgroundColor: '#ffffff',
      cursor: 'pointer',
    },
    
    '&.Mui-focused input, &.Mui-focused textarea': {
      color: theme.palette.primary.main,
    },
  },
  
  '& .MuiInputLabel-root': {
    display: 'none',
  },
  
  '& .MuiFormHelperText-root': {
    fontFamily: 'Area Normal, Inter, sans-serif',
    fontSize: '12px',
    fontWeight: 400,
    textTransform: 'none',
    marginLeft: 0,
    marginTop: '4px',
    
    '&.Mui-error': {
      color: tokens.colors.error,
    },
  },
  
  ...(success && {
    '& .MuiOutlinedInput-root fieldset': {
      borderColor: tokens.colors.grey,
      borderWidth: '1px',
    },
    '& .MuiFormHelperText-root': {
      color: tokens.colors.success,
    },
  }),
}));

const LabelContainer = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '6px',
}));

const StyledLabel = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '14px',
  fontWeight: 400,
  color: tokens.colors.grey,
}));

const OptionalTag = styled(Box)(() => ({
  fontFamily: 'Area Normal, Inter, sans-serif',
  fontSize: '12px',
  fontWeight: 500,
  letterSpacing: '0.06em',
  color: '#9E9E9E',
}));

export const TrackNowTextField = ({
  success,
  successMessage,
  helperText,
  label,
  required,
  disabled,
  error,
  showDisabledTooltip = true,
  ...props
}: TrackNowTextFieldProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const displayHelperText = success ? successMessage : helperText;
  
  const getEndAdornment = () => {
    if (success) {
      return (
        <InputAdornment position="end" sx={{ marginRight: '12px' }}>
          <CheckCircleOutlineIcon sx={{ color: tokens.colors.success, fontSize: '18px' }} />
        </InputAdornment>
      );
    }
    if (error) {
      return (
        <InputAdornment position="end" sx={{ marginRight: '12px' }}>
          <ReportProblemOutlinedIcon sx={{ color: tokens.colors.error, fontSize: '18px' }} />
        </InputAdornment>
      );
    }
    return props.InputProps?.endAdornment;
  };
  
  const fieldComponent = (
    <StyledTextField
      {...props}
      success={success}
      helperText={displayHelperText}
      variant="outlined"
      label={undefined}
      required={false}
      disabled={disabled}
      error={error}
      InputLabelProps={{ shrink: false }}
      InputProps={{
        ...props.InputProps,
        endAdornment: getEndAdornment(),
      }}
      onFocus={(e) => {
        setIsFocused(true);
        props.onFocus?.(e);
      }}
      onBlur={(e) => {
        setIsFocused(false);
        props.onBlur?.(e);
      }}
    />
  );
  
  return (
    <Box>
      {label && (
        <LabelContainer>
          <StyledLabel>
            {label}
            {required && <span style={{ color: tokens.colors.error, marginLeft: '4px' }}>*</span>}
          </StyledLabel>
          {!required && isFocused && <OptionalTag>Optional</OptionalTag>}
        </LabelContainer>
      )}
      {disabled && showDisabledTooltip ? (
        <Tooltip title="Complete required fields to enable this field" arrow>
          <Box>{fieldComponent}</Box>
        </Tooltip>
      ) : (
        fieldComponent
      )}
    </Box>
  );
};
